import { Form, Input, Select } from "antd"
import { MaskedInput } from "antd-mask-input"
import countryCodes, { type CountryProperty } from "country-codes-list"
import React, { useEffect, useState } from "react"

import { onRequiredTypeChange, removePunctuation } from "../../../helpers/format"
import validators from "../../../helpers/validators"
import { type ApplicationState } from "../../../store"
import KintoButton, { KintoButtonThemes } from "../../KintoButton"

const { Option } = Select

type CountryPhoneCode = { code: string, country: string }
export interface ClientInfoData {
  name: string
  cpf: string
  email: string
  phoneCountryCode: CountryPhoneCode
  phone: string
  company: string
}

type OwnProps = {
  onPlateChange?: (plate: string) => void
  afterFinish?: (formData: ClientInfoData) => void
  defaults?: ApplicationState["onefleetdevolucao"]
}

const ClientInfo = (props: OwnProps): React.ReactElement => {
  const [formData] = Form.useForm()
  const [phoneCountryCode, setPhoneCountryCode] = useState<CountryPhoneCode>(props.defaults?.data.client?.phoneCountryCode ?? { code: "55", country: "+55 Brasil" })

  function handleFormFinish () {
    if (props.afterFinish) {
      const {
        email,
        name,
        cpf,
        phone,
        company
      } = formData.getFieldsValue() as ClientInfoData

      props.afterFinish({
        email,
        name,
        cpf: removePunctuation(cpf),
        phone,
        phoneCountryCode,
        company
      })
    }
  }

  const handlePhoneChange = () => {
    const value = formData.getFieldValue("phone")

    if (value) {
      let initialValue = value.replace(/\D/g, "")
      if (initialValue.length > 11) initialValue = initialValue.replace(/(?<=\d{11})\d?/g, "")

      let formated = initialValue

      if (phoneCountryCode.code === "55") {
        formated = formated.replace(/^(\d{0,2})/, "($1) ")

        if (initialValue.length < 11) formated = formated.replace(/(?<=\(\d{2}\) )(\d{4})(?=\d)/, "$1-")
        else if (initialValue.length === 11) {
          formated = formated
            .replace(/(?<=\(\d{2}\) )(\d)/, "$1 ")
            .replace(/(?<=\(\d{2}\) \d )(\d{0,4})/, "$1-")
        }
      }

      formData.setFieldValue("phone", formated)
    }
  }

  useEffect(() => { handlePhoneChange() }, [phoneCountryCode])

  const countryCodeList = countryCodes.customList("countryCallingCode" as CountryProperty, "+{countryCallingCode} {countryNameLocal}")

  const selectCountry = (
    <Select
      defaultValue={phoneCountryCode.country}
      onChange={(value) => { setPhoneCountryCode(JSON.parse(value)) }}
      showSearch
      style={{ maxWidth: "20vw" }}
    >
      {
        Object.entries(countryCodeList).map(([key, value]) => (<Option key={key} value={JSON.stringify({ code: key, country: value })}>{value}</Option>))
      }
    </Select>
  )

  return (
    <Form
      onFinish={handleFormFinish}
      form={formData}
      layout="vertical"
      initialValues={props.defaults?.data.client}
      requiredMark={onRequiredTypeChange}
    >
      <Form.ErrorList />

      <Form.Item
        name="company"
        label="Nome da Empresa"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Nome da Empresa">
        </Input>
      </Form.Item>

      <Form.Item
        name="name"
        label="Nome completo do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          { pattern: /\w+ (?:\w+)+/, message: "Favor informar nome completo!" },
          { pattern: /^[a-zA-Z\u00C0-\u00FF ]+$/i, message: "Nome Inválido!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Nome Completo do Representante"
        />
      </Form.Item>

      <Form.Item
        name="cpf"
        label="CPF do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          {
            validator: async (_, value) => {
              if (value && !validators.cpf(value)) throw new Error("CPF Inválido!")
            }
          }
        ]}
      >
        <MaskedInput
          placeholder="CPF do Representante"
          size="large"
          mask="000.000.000-00"
        />
      </Form.Item>

      <Form.Item
        name="email"
        label="E-mail do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" },
          { pattern: /^[-_.\w]+@[-_.\w]+\.(?:[-_.\w]+\.?)+[-_.\w]$/, message: "E-mail inválido!" }
        ]}
      >
        <Input
          size="large"
          placeholder="E-mail do Representante"
        />
      </Form.Item>

      <Form.Item
        name="phone"
        label="Telefone do Representante"
        rules={[
          { required: true, message: "Campo obrigatório!" }
        ]}
      >
        <Input
          size="large"
          placeholder="Telefone do Representante"
          onChange={handlePhoneChange}
          addonBefore={selectCountry}>
        </Input>
      </Form.Item>

      <Form.Item label="" style={{ display: "flex", justifyContent: "right" }}>
        <KintoButton theme={KintoButtonThemes.contained} htmlType="submit">Próximo</KintoButton>
      </Form.Item>
    </Form>
  )
}

export default ClientInfo
